import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  Grid
} from '@mui/material'
import { CheckCircle as CompleteIcon } from '@mui/icons-material'
import { config } from '../../config/environment'

type MatchLevel = 'PRIMARY_MATCH' | 'SECONDARY_MATCH' | 'TERTIARY_MATCH' | 'NO_MATCH'

interface EvaluationStatus {
  evaluationId: number
  status: 'queued' | 'running' | 'completed' | 'failed'
  progress: number
  currentActivity: string
  statistics: {
    totalImages: number
    completed: number
    failed: number
    primaryMatch: number
    secondaryMatch: number
    tertiaryMatch: number
    noMatch: number
    lowIntensity: number
  }
}

interface EvaluationResultItem {
  id: number  // Evaluation result ID
  image_id: number
  filename: string
  s3_url: string
  provider: string
  tagged_emotion: string
  tagged_emotion_bucket?: string
  match_level: MatchLevel
  low_intensity_flag: boolean
  primary_emotion: string
  primary_emotion_bucket?: string
  primary_intensity: number
  primary_confidence: number
  primary_reasoning: string
  primary_emotion_distribution_pct?: number
  secondary_emotion: string
  secondary_emotion_bucket?: string
  secondary_intensity: number
  secondary_confidence?: number
  secondary_reasoning?: string
  secondary_emotion_distribution_pct?: number
  tertiary_emotion: string
  tertiary_emotion_bucket?: string
  tertiary_intensity: number
  tertiary_confidence?: number
  tertiary_reasoning?: string
  tertiary_emotion_distribution_pct?: number
  visual_elements?: {
    dominant_colors: string[]
    lighting_quality: string
    composition_style: string
    key_subjects: string[]
  }
  emotional_clarity: number
  overall_emotional_intensity?: number
  overall_arousal_level?: number
  overall_valence?: number
  overall_complexity_score?: number
  marketing_applications?: string[]
  mismatch_reasoning?: string
  processing_time_ms: number
  llm_model: string
  processed_at: string
  prompt_id?: string
  prompt_name?: string
  primary_taxonomy_verified?: boolean | null
  secondary_taxonomy_verified?: boolean | null
  tertiary_taxonomy_verified?: boolean | null
  taxonomy_validation_issues?: Array<{
    code: string
    slot: 'primary' | 'secondary' | 'tertiary'
    receivedValue: unknown
    reason: string
    configurationKey?: string
    configurationVersion?: number
    attemptNumber?: number
  }>
  verification_attempts?: Array<{
    passNumber: number
    passed: boolean
    issues: Array<{code: string; slot: string; receivedValue: unknown; reason: string}>
    error?: string
  }>
  reevaluation_count?: number
  sent_for_reevaluation?: boolean
  human_verification_needed?: boolean
}

interface EvaluationResult {
  id: number
  name: string
  description?: string
  organization_name?: string
  prompt_id?: string
  prompt_type?: 'system' | 'user'
  prompt_name?: string
  status: string
  total_images: number
  progress: number
  created_at: string
  completed_at?: string
  deterministic_verification_enabled?: boolean
  reevaluation_round_count?: number
  was_reevaluated?: boolean
  evaluation_pass_count?: number
  configuration_hash?: string
  configuration_snapshot?: {
    scope: 'global' | 'organization'
    key: string
    version: number
    name: string
  }
  filter_config?: any
  stats_completed?: number
  stats_failed?: number
  stats_primary_match?: number
  stats_secondary_match?: number
  stats_tertiary_match?: number
  stats_no_match?: number
  stats_low_intensity?: number
  stats_avg_emotional_clarity?: number
  tokenUsageSummary?: {
    inputTokens: number
    outputTokens: number
    totalTokens: number
    cacheCreationInputTokens?: number
    cacheReadInputTokens?: number
  }
  results: EvaluationResultItem[]
}

interface EvaluationProcessingProps {
  evaluationId: string
  onGetEvaluationResults: (evaluationId: string) => Promise<EvaluationResult>
  onComplete: (results: EvaluationResult) => void
  onError: (error: string) => void
}

const EvaluationProcessing: React.FC<EvaluationProcessingProps> = ({
  evaluationId,
  onGetEvaluationResults,
  onComplete,
  onError
}) => {
  const [evaluationStatus, setEvaluationStatus] = useState<EvaluationStatus | null>(null)
  const evaluationStatusRef = React.useRef<EvaluationStatus | null>(null)
  const [eventSource, setEventSource] = useState<EventSource | null>(null)

  useEffect(() => {
    if (!evaluationId || eventSource) return

    const sse = new EventSource(`${config.API_BASE_URL}/api/evaluations/${evaluationId}/stream`, { withCredentials: true })

    sse.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        const statusUpdate: EvaluationStatus = {
          evaluationId: data.evaluationId,
          status: data.status,
          progress: data.progress,
          currentActivity: data.currentActivity,
          statistics: {
            totalImages: data.statistics.totalImages,
            completed: data.statistics.completed,
            failed: data.statistics.failed,
            primaryMatch: data.statistics.primaryMatch || 0,
            secondaryMatch: data.statistics.secondaryMatch || 0,
            tertiaryMatch: data.statistics.tertiaryMatch || 0,
            noMatch: data.statistics.noMatch || 0,
            lowIntensity: data.statistics.lowIntensity || 0
          }
        }

        setEvaluationStatus(statusUpdate)
        evaluationStatusRef.current = statusUpdate

        if (['completed', 'failed'].includes(statusUpdate.status)) {
          if (statusUpdate.status === 'completed') {
            onGetEvaluationResults(evaluationId)
              .then(results => {
                onComplete(results)
                sse.close()
                setEventSource(null)
              })
              .catch(err => {
                console.error('Failed to fetch evaluation results:', err)
                onError('Failed to fetch evaluation results')
                sse.close()
                setEventSource(null)
              })
          } else if (statusUpdate.status === 'failed') {
            onError('Evaluation failed to complete')
            sse.close()
            setEventSource(null)
          }
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error)
        onError('Failed to process evaluation update')
      }
    }

    sse.onerror = () => {
      console.error('SSE connection error')
      sse.close()
      setEventSource(null)
      if (evaluationStatusRef.current?.status !== 'completed') {
        onError('Lost connection to evaluation process')
      }
    }

    setEventSource(sse)

    return () => {
      if (sse.readyState !== EventSource.CLOSED) {
        sse.close()
      }
      setEventSource(null)
    }
  }, [evaluationId, onGetEvaluationResults, onComplete, onError])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'primary'
      case 'completed': return 'success'
      case 'failed': return 'error'
      case 'cancelled': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CompleteIcon color="success" />
      default: return undefined
    }
  }

  if (!evaluationStatus) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Initializing Evaluation...
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>
        Evaluating Images
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">
              Evaluation Status
            </Typography>
            <Chip
              label={evaluationStatus.status.toUpperCase()}
              color={getStatusColor(evaluationStatus.status) as any}
              icon={getStatusIcon(evaluationStatus.status)}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {evaluationStatus.currentActivity}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={evaluationStatus.progress}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" sx={{ mb: 2 }}>
            {evaluationStatus.progress}% complete
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Total</Typography>
                <Typography variant="h6">{evaluationStatus.statistics.totalImages}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Processed</Typography>
                <Typography variant="h6">{evaluationStatus.statistics.completed}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Failed</Typography>
                <Typography variant="h6" color="error.main">{evaluationStatus.statistics.failed}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Low Intensity</Typography>
                <Typography variant="h6" color="warning.main">{evaluationStatus.statistics.lowIntensity}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Match Distribution</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="body2" color="success.dark">Primary</Typography>
                <Typography variant="h6" color="success.main">{evaluationStatus.statistics.primaryMatch}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="body2" color="info.dark">Secondary</Typography>
                <Typography variant="h6" color="info.main">{evaluationStatus.statistics.secondaryMatch}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">Tertiary</Typography>
                <Typography variant="h6" color="warning.main">{evaluationStatus.statistics.tertiaryMatch}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography variant="body2" color="error.dark">No Match</Typography>
                <Typography variant="h6" color="error.main">{evaluationStatus.statistics.noMatch}</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {evaluationStatus.status === 'failed' && (
        <Alert severity="error">
          The evaluation job failed to complete. Please try again.
        </Alert>
      )}
    </Box>
  )
}

export default EvaluationProcessing
export type { EvaluationResult, EvaluationResultItem, MatchLevel }
